/**
 * Auth persistence helpers.
 *
 * BUG-004/005: JWT tokens are NO LONGER stored in localStorage/sessionStorage.
 * The auth token lives exclusively in an httpOnly cookie set by the backend
 * (see backend/utils/sendToken.js). The browser sends it automatically on
 * every request when `withCredentials: true` is set on the axios instance.
 *
 * We still persist non-sensitive user metadata (userId, display name, role)
 * so the UI can render without an extra /me round-trip on page load.
 *
 * The app supports a "remember me" behavior by choosing between `localStorage`
 * (persistent) and `sessionStorage` (cleared when the tab/window closes).
 *
 * All storage reads/writes are wrapped in try/catch because some environments
 * (private mode, embedded webviews, strict browser settings) can throw on access.
 */

export const getStoredToken = () => {
  // Token is now in httpOnly cookie only - not accessible from JS.
  // The cookie is sent automatically with withCredentials: true.
  return null;
};

/**
 * Check whether a value looks like a real JWT.
 *
 * We keep this intentionally loose so it accepts the legacy token format
 * stored by older builds, but rejects boolean flags like `true`.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const isLikelyJwtToken = (value) =>
  typeof value === 'string' &&
  value.split('.').length === 3 &&
  value.length > 20;

const getAvailableStorage = (storage) => (typeof storage !== 'undefined' ? storage : null);

/**
 * Clear the client-side auth session hints persisted for UI bootstrap.
 *
 * This intentionally does not try to remove the httpOnly auth cookie because
 * the frontend cannot access it directly. The backend owns cookie invalidation.
 */
export const clearAuthStorage = () => {
  const authKeys = ['userId', 'userData', 'token'];
  [
    getAvailableStorage(typeof localStorage !== 'undefined' ? localStorage : null),
    getAvailableStorage(typeof sessionStorage !== 'undefined' ? sessionStorage : null),
  ].forEach((storage) => {
    if (!storage) return;
    authKeys.forEach((key) => {
      try {
        storage.removeItem(key);
      } catch (_) {
        // Ignore storage access errors
      }
    });
  });
};

/**
 * Persist auth-related data into either localStorage or sessionStorage.
 *
 * IMPORTANT: Only non-sensitive display data is stored (userId, userData without token).
 * The JWT token is managed exclusively via httpOnly cookies by the backend.
 *
 * @param {{ userData?: any, rememberMe?: boolean }} params
 */
export const setAuthStorage = ({ userData, rememberMe = true }) => {
  const availableLocalStorage = getAvailableStorage(typeof localStorage !== 'undefined' ? localStorage : null);
  const availableSessionStorage = getAvailableStorage(typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  const primaryStorage = rememberMe ? availableLocalStorage : availableSessionStorage;
  const secondaryStorage = rememberMe ? availableSessionStorage : availableLocalStorage;
  const authKeys = ['userId', 'userData'];

  // Clear the secondary storage to avoid conflicting sessions.
  if (secondaryStorage) {
    authKeys.forEach((key) => {
      try {
        secondaryStorage.removeItem(key);
      } catch (_) {
        // Ignore storage access errors
      }
    });
  }

  // Also clear any legacy 'token' keys that may still exist from before BUG-004/005 fix.
  try { secondaryStorage?.removeItem('token'); } catch (_) {}
  try { primaryStorage?.removeItem('token'); } catch (_) {}

  if (userData) {
    // Store user metadata for UI (no token - that's in httpOnly cookie)
    const safeUserData = { ...userData };
    delete safeUserData.token;

    const userId = safeUserData._id || safeUserData.id || '';
    if (userId && primaryStorage) {
      try {
        primaryStorage.setItem('userId', userId);
      } catch (_) {
        // Ignore storage access errors
      }
    }
    if (primaryStorage) {
      try {
        primaryStorage.setItem('userData', JSON.stringify(safeUserData));
      } catch (_) {
        // Ignore storage access errors
      }
    }
  }
};
